// src/components/BaseDeDatos.jsx
import React, { useState } from 'react';
import { Plus, ChevronDown, Database, Check, FileEdit, Trash2, ChevronUp } from 'lucide-react';

const initialConnections = [
  { name: 'casos_activos', type: 'PostgreSQL', records: '156 registros', status: 'connected' },
  { name: 'clientes_seguros', type: 'MongoDB', records: '89 registros', status: 'connected' },
  { name: 'documentos_legales', type: 'MySQL', records: '1.247 registros', status: 'connected' },
];

const ConnectionItem = ({ connection, onEdit, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-gray-700 rounded-xl shadow-md p-4 mb-3 border border-gray-600 text-gray-200">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center space-x-3">
          <span className={`w-3 h-3 rounded-full ${connection.status === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></span>
          <h4 className="text-lg font-semibold">{connection.name}</h4>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(connection.name); }}
            className="text-gray-400 hover:text-gray-100 p-2 rounded-full hover:bg-gray-600 transition-colors duration-200"
            title="Editar conexión"
          >
            <FileEdit size={18} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(connection.name); }}
            className="text-gray-400 hover:text-red-400 p-2 rounded-full hover:bg-gray-600 transition-colors duration-200"
            title="Eliminar conexión"
          >
            <Trash2 size={18} />
          </button>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      {isExpanded && (
        <div className="pl-6 mt-2 text-gray-400 text-sm space-y-1">
          <div className="flex items-center space-x-2">
            <Database size={16} />
            <span>{connection.type}</span>
          </div>
          <p>{connection.records}</p>
        </div>
      )}
    </div>
  );
};

const BaseDeDatos = () => {
  const [connections, setConnections] = useState(initialConnections);
  const [sqlQuery, setSqlQuery] = useState(`SELECT *
FROM casos_legales
WHERE estado = "en progreso"
AND fecha_limite < CURRENT_DATE + 7
ORDER BY fecha_limite ASC;`);

  const handleAddConnection = () => {
    const newConnection = {
      name: 'nueva_conexion',
      type: 'Tipo de BD',
      records: '0 registros',
      status: 'connected',
    };

    setConnections(prevConnections => [...prevConnections, newConnection]);
    setTimeout(() => {
      handleEditConnection(newConnection.name);
    }, 0);
  };

  const handleEditConnection = (nameToEdit) => {
    const connectionToEdit = connections.find(conn => conn.name === nameToEdit);
    
    if (!connectionToEdit) return;

    const newName = window.prompt('Introduce el nuevo nombre:', connectionToEdit.name);
    const newType = window.prompt('Introduce el nuevo tipo:', connectionToEdit.type);
    const newRecords = window.prompt('Introduce el número de registros:', connectionToEdit.records);

    if (newName || newType || newRecords) {
      setConnections(prevConnections =>
        prevConnections.map(conn =>
          conn.name === nameToEdit
            ? {
                ...conn,
                name: newName !== null ? newName : conn.name,
                type: newType !== null ? newType : conn.type,
                records: newRecords !== null ? newRecords : conn.records,
              }
            : conn
        )
      );
    }
  };

  const handleDeleteConnection = (nameToDelete) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la conexión "${nameToDelete}"?`)) {
      setConnections(connections.filter(conn => conn.name !== nameToDelete));
    }
  };

  const handleExecuteQuery = () => {
    alert('Consulta ejecutada con éxito. Resultados simulados en la consola.');
    console.log(`Ejecutando consulta:\n${sqlQuery}`);
  };

  return (
    <div className="p-8 bg-gray-900 min-h-screen text-gray-100">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-100">Base de Datos LegalPlus</h1>
        <button
          onClick={handleAddConnection}
          className="bg-blue-800 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-xl transition-colors duration-300 flex items-center"
        >
          <Plus size={20} className="mr-2" />
          Nueva Conexión
        </button>
      </div>

      <div className="space-y-6">
        {/* Conexiones */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-between text-blue-400 mb-4">
            <h3 className="text-xl font-bold">Conexiones</h3>
          </div>
          {connections.map((conn, index) => (
            <ConnectionItem
              key={index}
              connection={conn}
              onEdit={handleEditConnection}
              onDelete={handleDeleteConnection}
            />
          ))}
        </div>

        {/* Editor SQL */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-between text-blue-400 mb-4">
            <h3 className="text-xl font-bold">Editor SQL</h3>
            <button
              onClick={handleExecuteQuery}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-xl flex items-center transition-colors duration-300"
            >
              <Check size={20} className="mr-2" />
              Ejecutar
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-4 text-sm font-mono text-green-400">
            <textarea
              className="w-full h-40 bg-transparent outline-none resize-none text-gray-200"
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseDeDatos;